import { useMemo } from 'react';

import SubActivityPrint from '../../sub-activity/sub-activity-print';
import InteractiveDragDisplay from '../../shared/interactive-drag-drop/interactive-drag-display';
import {
  InteractiveResponseAnswerIcons,
  InteractiveResponseTitle,
  InteractiveResponseViewScaler,
} from '../shared/interactive-response-shared';
import { InteractiveResponseContainer } from '../interactive-response.style';
import {
  ViewProps,
  RESPONSE_FREE,
} from './settings/interactive-response.constants';
import {
  getAnswers,
  initialise,
} from './play/interactive-response-sequence.utils';
import { ComponentAspectRatio } from '../../shared/component-height/component-dimensions';
import { ComponentAbsoluteWrapper } from '../../shared/component-height/component-dimensions.style';
import { useContainerDimensions } from '../../shared/component-height/component-dimensions.hooks';
import { useActivityAnswersPrintContext } from 'approot/activity-builder/print/answers/activity-answers-print.context';
import { defaultDimensions } from '../interactive-response-shared.constants';
import InteractiveSequenceDropDisplay from './play/interactive-sequence-drop-display';
import type { InteractiveResponseAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import * as Styled from './interactive-response-sequence-print.style';
import { Box } from 'approot/shared/design-system/box/box.style';

const InteractiveResponseSequencePrintAnswers = ({
  properties,
  subActivityProperties,
  id,
  studentId,
}: ViewProps & {
  studentId?: number;
}) => {
  const { answers, showAnswer } = useActivityAnswersPrintContext(id);

  const answer = useMemo(
    () =>
      studentId
        ? (answers?.find(a => a.studentId === studentId)
            ?.answer as InteractiveResponseAnswerFragment)
        : (answers?.[0]?.answer as InteractiveResponseAnswerFragment),
    [answers, studentId]
  );

  const { interactiveResponse } = properties;
  const isFreeResponse = interactiveResponse.answerType === RESPONSE_FREE;

  const { dragItems, dropItems } = useMemo(() => {
    return initialise(interactiveResponse.variant, interactiveResponse.items);
  }, [interactiveResponse.variant, interactiveResponse.items]);

  const parsedAnswers = useMemo(() => {
    if (!answer) {
      return null;
    }
    return getAnswers(
      answer,
      interactiveResponse.items,
      interactiveResponse.variant
    );
  }, [interactiveResponse.items, answer, interactiveResponse.variant]);

  const { setContainerElement } = useContainerDimensions({
    aspectRatio: 16 / 9,
  });

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <Box $justifyContent="center">
        <InteractiveResponseContainer>
          <ComponentAspectRatio
            dimensions={properties.responseDimensionData || defaultDimensions}
          >
            <ComponentAbsoluteWrapper ref={setContainerElement}>
              <InteractiveResponseViewScaler>
                <InteractiveResponseTitle
                  title={interactiveResponse.title}
                  subTitle={interactiveResponse.subTitle}
                />
                {parsedAnswers ? (
                  <Styled.InteractiveResponsePrintDropZoneOuter>
                    <Styled.InteractiveResponsePrintDropZone>
                      {parsedAnswers.map(answer => (
                        <Styled.InteractiveResponsePrintDropZoneItem
                          key={answer.id}
                          $labelLength={interactiveResponse.labelLength}
                        >
                          <InteractiveSequenceDropDisplay
                            label={answer.label}
                            labelLength={interactiveResponse.labelLength}
                            labelBorder={interactiveResponse.labelBorder}
                            isCorrect={
                              isFreeResponse || !showAnswer
                                ? undefined
                                : answer.isCorrect
                            }
                            width={88}
                            height={88}
                          >
                            <InteractiveDragDisplay
                              scaleTo={88}
                              imageUrl={answer.image}
                              isFit={answer.isFit}
                              description={answer.description}
                              baseColour={answer.baseColour}
                              variant={
                                answer.variant || interactiveResponse.variant
                              }
                              text={answer.text}
                              richtext={answer.richtext}
                            />
                            {!isFreeResponse && showAnswer && (
                              <InteractiveResponseAnswerIcons
                                isCorrect={answer.isCorrect}
                                animate={false}
                              />
                            )}
                          </InteractiveSequenceDropDisplay>
                        </Styled.InteractiveResponsePrintDropZoneItem>
                      ))}
                    </Styled.InteractiveResponsePrintDropZone>
                  </Styled.InteractiveResponsePrintDropZoneOuter>
                ) : (
                  <>
                    <Styled.InteractiveResponsePrintDragZone>
                      {dragItems.map((dragItem, index) => (
                        <Styled.InteractiveResponsePrintDragZoneItem
                          key={`${dragItem.id}-${index}`}
                        >
                          <InteractiveDragDisplay
                            imageUrl={dragItem.question.image?.small}
                            isFit={dragItem.question.image?.isFit}
                            description={
                              dragItem.question.image?.description || ''
                            }
                            baseColour={dragItem.question.baseColour}
                            variant={dragItem.question.variant}
                            text={dragItem.question.text}
                            richtext={dragItem.question.richtext}
                          />
                        </Styled.InteractiveResponsePrintDragZoneItem>
                      ))}
                    </Styled.InteractiveResponsePrintDragZone>
                    <Styled.InteractiveResponsePrintDropZoneOuter>
                      <Styled.InteractiveResponsePrintDropZone>
                        {dropItems.map(dropItem => (
                          <Styled.InteractiveResponsePrintDropZoneItem
                            key={dropItem.id}
                            $labelLength={interactiveResponse.labelLength}
                          >
                            <InteractiveSequenceDropDisplay
                              label={dropItem.label}
                              labelLength={interactiveResponse.labelLength}
                              labelBorder={interactiveResponse.labelBorder}
                            />
                          </Styled.InteractiveResponsePrintDropZoneItem>
                        ))}
                      </Styled.InteractiveResponsePrintDropZone>
                    </Styled.InteractiveResponsePrintDropZoneOuter>
                  </>
                )}
              </InteractiveResponseViewScaler>
            </ComponentAbsoluteWrapper>
          </ComponentAspectRatio>
        </InteractiveResponseContainer>
      </Box>
    </SubActivityPrint>
  );
};

export default InteractiveResponseSequencePrintAnswers;
